import { useState } from "react"
import { Minus, Plus } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

interface NumberIncrementerProps {
  value: number
  min?: number
  max?: number
  step?: number
  onIncrement?: (value: number) => void
  onDecrement?: (value: number) => void
  onChange?: (value: number) => void
  className?: string
  disabled?: boolean
}

export function NumberIncrementer({
  value: initialValue = 1,
  min = 0,
  max = 99,
  step = 1,
  onIncrement,
  onDecrement,
  onChange,
  className,
  disabled = false,
}: NumberIncrementerProps) {
  const [value, setValue] = useState(initialValue)

  const handleIncrement = () => {
    if (value < max) {
      const newValue = value + step
      setValue(newValue)
      onIncrement?.(newValue)
      onChange?.(newValue)
    }
  }

  const handleDecrement = () => {
    if (value > min) {
      const newValue = value - step
      setValue(newValue)
      onDecrement?.(newValue)
      onChange?.(newValue)
    }
  }

  return (
    <div
      className={cn(
        "flex items-center h-11 border border-gray-200 rounded-xl",
        className
      )}
      data-testid="number-incrementer"
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-full rounded-l-xl border-r border-gray-200"
        onClick={(e) => {
          e.stopPropagation()
          handleDecrement()
        }}
        disabled={disabled || value <= min}
        aria-label="Decrease value"
        data-testid="decrement-button"
      >
        <Minus size={16} className="text-gray-500" aria-hidden="true" />
      </Button>

      <div className="flex items-center justify-center w-10">
        <span className="text-sm font-medium" data-testid="current-value">
          {value}
        </span>
      </div>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-full rounded-r-xl border-l border-gray-200"
        onClick={(e) => {
          e.stopPropagation()
          handleIncrement()
        }}
        disabled={disabled || value >= max}
        aria-label="Increase value"
        data-testid="increment-button"
      >
        <Plus size={16} className="text-gray-500" aria-hidden="true" />
      </Button>
    </div>
  )
}
